import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

/**
 * MentorDocumentsPage — read-only document viewer for the mentor role.
 *
 * Fetches documents from `GET /api/mentors/documents`, displays a list
 * with filename, last modified date, and file size. PDF documents get
 * an inline preview; non-PDF files open in a new tab.
 *
 * Error handling:
 *   - Any HTTP error or network error → error message + retry (R6.6)
 *
 * No edit/delete controls are rendered — strictly read-only (R6.2).
 *
 * Requirements: 6.1–6.7
 */

export interface MentorDocument {
  filename: string;
  last_modified: string; // ISO 8601
  size_bytes: number;
  presigned_url: string;
}

interface DocumentsResponse {
  documents: MentorDocument[];
}

type FetchState =
  | { status: 'loading' }
  | { status: 'success'; documents: MentorDocument[] }
  | { status: 'error'; message: string };

export default function MentorDocumentsPage() {
  const [state, setState] = useState<FetchState>({ status: 'loading' });
  const [previewDoc, setPreviewDoc] = useState<MentorDocument | null>(null);

  const fetchDocuments = useCallback(async () => {
    setState({ status: 'loading' });
    setPreviewDoc(null);
    try {
      const data = await api.get<DocumentsResponse>('/api/mentors/documents');
      setState({ status: 'success', documents: data.documents });
    } catch {
      setState({
        status: 'error',
        message: 'Documents could not be loaded.',
      });
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // R6.7: Loading indicator
  if (state.status === 'loading') {
    return (
      <div aria-label="Loading documents">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Documents</h1>
        <div className="animate-pulse space-y-3">
          <div className="h-12 bg-gray-200 rounded w-full" />
          <div className="h-12 bg-gray-200 rounded w-full" />
          <div className="h-12 bg-gray-200 rounded w-full" />
        </div>
      </div>
    );
  }

  // R6.6: Error state with retry
  if (state.status === 'error') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Documents</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <p className="text-red-700">{state.message}</p>
          <button
            onClick={fetchDocuments}
            className="mt-3 text-sm font-medium text-red-700 hover:text-red-900 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // R6.4: Empty state
  if (state.documents.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Documents</h1>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center" role="status">
          <p className="text-gray-600">No documents have been generated yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Documents</h1>

      <div className="flex gap-6">
        {/* Document list */}
        <div className={previewDoc ? 'w-1/2' : 'w-full'}>
          <ul className="space-y-2" aria-label="Document list">
            {state.documents.map((doc) => (
              <li key={doc.filename}>
                <DocumentRow
                  document={doc}
                  isSelected={previewDoc?.filename === doc.filename}
                  onSelect={() => handleDocumentSelect(doc)}
                />
              </li>
            ))}
          </ul>
        </div>

        {/* Inline PDF preview panel (R6.3) */}
        {previewDoc && isPdf(previewDoc.filename) && (
          <div className="w-1/2">
            <div className="border border-gray-200 rounded-lg overflow-hidden sticky top-6">
              <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-700 truncate">
                  {previewDoc.filename}
                </span>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  aria-label="Close preview"
                >
                  Close
                </button>
              </div>
              <iframe
                src={previewDoc.presigned_url}
                className="w-full h-[600px]"
                title={`Preview of ${previewDoc.filename}`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function handleDocumentSelect(doc: MentorDocument) {
    if (isPdf(doc.filename)) {
      // R6.3: PDF → inline preview
      setPreviewDoc(doc);
    } else {
      // R6.3: Non-PDF → open in new tab
      window.open(doc.presigned_url, '_blank', 'noopener,noreferrer');
    }
  }
}

/** Document row — displays filename, last modified, and file size (R6.1) */
function DocumentRow({
  document,
  isSelected,
  onSelect,
}: {
  document: MentorDocument;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
        isSelected
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
      aria-pressed={isSelected}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{document.filename}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatDate(document.last_modified)} · {formatFileSize(document.size_bytes)}
          </p>
        </div>
        <span className="ml-3 text-xs text-gray-400 flex-shrink-0">
          {isPdf(document.filename) ? 'Preview' : 'Open'}
        </span>
      </div>
    </button>
  );
}

/** Check if a filename refers to a PDF */
export function isPdf(filename: string): boolean {
  return filename.toLowerCase().endsWith('.pdf');
}

/** Format bytes into a human-readable file size */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Format ISO date string to a readable locale date */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
